import { InputChangeEventDetail, IonInputCustomEvent } from '@ionic/core';
import {
  IonButton,
  IonContent,
  IonFooter,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonPopover,
  useIonRouter,
} from '@ionic/react';
import { informationCircleOutline } from 'ionicons/icons';
import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { t } from 'ttag';

import TabLayout from '@components/TabLayout';
import RoutePath from '@custom-types/RoutePath';
import useLoading from '@hooks/useLoading';
import { usePrinter } from '@hooks/usePrinter';
import { useEventAndConfiguration } from '@pages/Event/queries';
import { generateRoute } from '@services/navigation-service';

const Footer = styled(IonFooter)`
  padding: 32px;
`;

const Input = styled(IonInput)<{ $danger?: boolean }>`
  ${props => props.$danger && '--color: var(--ion-color-danger);'}
  .error-text {
    padding-bottom: 8px;
  }
`;

const Settings = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [{ data: event, isFetching: isEventFetching }] = useEventAndConfiguration(eventId);
  const {
    isLoadingSettings,
    saveSettings,
    settings,
    selectedPrinter,
    availablePrinters,
    needsSetup: showPrinterSelectionError,
  } = usePrinter();
  const { push: pushRoute } = useIonRouter();

  useLoading(isEventFetching || isLoadingSettings);

  useEffect(() => {
    if (!selectedPrinter && availablePrinters.length > 0) {
      saveSettings({ ...settings, printer: availablePrinters[0] });
    }
  }, [availablePrinters, saveSettings, selectedPrinter, settings]);

  if (!event || !settings) {
    return null;
  }

  return (
    <TabLayout title={event.name}>
      <IonContent>
        <IonList>
          <IonListHeader>
            <IonLabel>{t`Printer Settings`}</IonLabel>
          </IonListHeader>
          <IonItem
            button
            onClick={() => pushRoute(generateRoute(RoutePath.PrinterSelection, { eventId }))}
          >
            <Input
              label={t`Printer`}
              labelPlacement="stacked"
              $danger={!selectedPrinter}
              value={settings.printer?.serialNumber || t`No printer selected`}
              errorText={showPrinterSelectionError ? t`Unable to connect to Printer.` : undefined}
              className={`ion-touched ${showPrinterSelectionError ? 'ion-invalid' : 'ion-valid'}`}
              readonly
            />
          </IonItem>
          <IonItem>
            <Input
              label={t`Paper Size`}
              labelPlacement="stacked"
              value={t`H 2.4" | 62 mm continuous roll`}
              readonly
              disabled
            />
            <IonIcon
              id="paper-info"
              size="small"
              slot="end"
              icon={informationCircleOutline}
              aria-hidden="true"
            />
            <IonPopover trigger="paper-info" triggerAction="click">
              <IonContent class="ion-padding">{t`We currently only support one paper size.`}</IonContent>
            </IonPopover>
          </IonItem>
          <IonItem>
            <Input
              label={t`Copies`}
              labelPlacement="stacked"
              placeholder="1"
              type="number"
              inputmode="numeric"
              value={settings.numCopies}
              onIonChange={(e: IonInputCustomEvent<InputChangeEventDetail>) => {
                // Min and Max properties don't work for IonInput, so we have to do this manually.
                // If we're passed an invalid value like 0 or -5, we'll default it back to 1
                let numCopies = parseInt(e.detail.value!, 10);
                if (!numCopies || numCopies < 1) {
                  numCopies = 1;
                }
                // If we're passed a value greater than 9999, we'll default it back to 9999
                if (numCopies > 9999) {
                  numCopies = 9999;
                }
                saveSettings({ ...settings, numCopies });
              }}
            />
          </IonItem>
        </IonList>
      </IonContent>
      <Footer>
        <IonButton
          expand="block"
          fill="outline"
          onClick={() => pushRoute(generateRoute(RoutePath.PrintBadgeTest, { eventId }))}
          disabled={showPrinterSelectionError}
        >
          {t`Print Test Badge`}
        </IonButton>
      </Footer>
    </TabLayout>
  );
};

export default Settings;
